import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Search, Sparkles, Loader2, Check, Image as ImageIcon, Camera } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ImageSearchModal — Searches Unsplash/Pexels and uses AI keyword suggestions.
 * Returns picked image URLs via onPick(url[]).
 */
export function ImageSearchModal({ open, onOpenChange, adTitle = '', adCategory = '', onPick }) {
    const [q, setQ] = useState('');
    const [provider, setProvider] = useState('unsplash');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [message, setMessage] = useState('');
    const [aiKeywords, setAiKeywords] = useState([]);
    const [aiLoading, setAiLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setQ(adTitle || '');
            setResults([]);
            setSelected(new Set());
            setAiKeywords([]);
            setMessage('');
        }
    }, [open, adTitle]);

    const doSearch = async (query = q, prov = provider) => {
        if (!query || !query.trim()) { toast.error('Escribe algo para buscar'); return; }
        setLoading(true);
        setResults([]);
        try {
            const { data } = await api.get(`/media/search/${prov}`, { params: { q: query.trim(), per_page: 24 } });
            setResults(data.results || []);
            setMessage(data.message || (data.configured ? '' : 'Modo demo (sin API key configurada).'));
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setLoading(false); }
    };

    const suggestKeywords = async () => {
        setAiLoading(true);
        try {
            const { data } = await api.post('/media/ai-suggest-keywords', { title: adTitle, category: adCategory });
            setAiKeywords(data.keywords || []);
            if (data.source === 'fallback') toast.info('IA no configurada, usando palabras clave locales');
        } catch (e) { toast.error('Error IA'); }
        finally { setAiLoading(false); }
    };

    const toggle = (url) => {
        const s = new Set(selected);
        if (s.has(url)) s.delete(url); else s.add(url);
        setSelected(s);
    };

    const pick = () => {
        onPick && onPick(Array.from(selected));
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="image-search-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Search className="h-5 w-5 text-red-600" />Buscador de imágenes con IA</DialogTitle>
                    <DialogDescription>Busca en Unsplash o Pexels y añade fotos profesionales a tu anuncio.</DialogDescription>
                </DialogHeader>

                <Tabs value={provider} onValueChange={setProvider}>
                    <TabsList>
                        <TabsTrigger value="unsplash" data-testid="src-unsplash">Unsplash</TabsTrigger>
                        <TabsTrigger value="pexels" data-testid="src-pexels">Pexels</TabsTrigger>
                    </TabsList>
                </Tabs>

                <div className="flex gap-2">
                    <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doSearch(); } }} placeholder="Ej: laptop moderno oficina" data-testid="img-search-input" />
                    <Button onClick={() => doSearch()} disabled={loading} className="bg-red-600 hover:bg-red-700" data-testid="img-search-btn">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                    <Button onClick={suggestKeywords} disabled={aiLoading} variant="outline" data-testid="ai-suggest-btn">
                        {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" />IA</>}
                    </Button>
                </div>

                {aiKeywords.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="ai-keywords">
                        <span className="text-xs text-muted-foreground self-center">Sugerencias IA:</span>
                        {aiKeywords.map((k, i) => (
                            <Button key={i} size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => { setQ(k); doSearch(k); }}>
                                {k}
                            </Button>
                        ))}
                    </div>
                )}

                {message && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded p-2">{message}</p>}

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="img-search-results">
                    {results.length === 0 && !loading && (
                        <div className="col-span-full text-center text-sm text-muted-foreground py-8">
                            <ImageIcon className="h-8 w-8 mx-auto opacity-40 mb-2" />
                            Escribe una búsqueda o usa las sugerencias IA.
                        </div>
                    )}
                    {results.map(r => {
                        const isSel = selected.has(r.url);
                        return (
                            <button
                                key={r.id}
                                onClick={() => toggle(r.url)}
                                type="button"
                                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${isSel ? 'border-red-600 ring-2 ring-red-300' : 'border-transparent hover:border-red-400'}`}
                                data-testid={`img-result-${r.id}`}
                            >
                                <img src={r.thumb} alt={r.author} className="h-full w-full object-cover" />
                                {isSel && <div className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600 flex items-center justify-center"><Check className="h-4 w-4 text-white" /></div>}
                                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
                                    <div className="text-[10px] text-white truncate">© {r.author}</div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-xs text-muted-foreground">{selected.size} seleccionadas</div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                        <Button onClick={pick} disabled={selected.size === 0} className="bg-red-600 hover:bg-red-700" data-testid="add-selected-images">
                            <Camera className="h-4 w-4 mr-1" />Añadir al anuncio ({selected.size})
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default ImageSearchModal;
